'use client';

export interface NotificationSettings {
  callbackRequests: boolean;
  missedCalls: boolean;
  receivedMessages: boolean;
  viewedPhotos: boolean;
  tabletConnection: boolean;
  calendarReminders: boolean;
  inApp: boolean;
  sound: boolean;
  emailSummary: boolean;
}

export interface PrivacySettings {
  activityHistoryEnabled: boolean;
}

export interface FamilySettings {
  notifications: NotificationSettings;
  privacy: PrivacySettings;
}

const STORAGE_KEY = 'lucat_family_settings';

const DEFAULT_SETTINGS: FamilySettings = {
  notifications: {
    callbackRequests: true,
    missedCalls: true,
    receivedMessages: true,
    viewedPhotos: true,
    tabletConnection: true,
    calendarReminders: true,
    inApp: true,
    sound: false,
    emailSummary: false,
  },
  privacy: {
    activityHistoryEnabled: true,
  },
};

function loadSettings(): FamilySettings {
  if (typeof window === 'undefined') return DEFAULT_SETTINGS;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<FamilySettings>;
      return {
        notifications: { ...DEFAULT_SETTINGS.notifications, ...parsed.notifications },
        privacy: { ...DEFAULT_SETTINGS.privacy, ...parsed.privacy },
      };
    }
  } catch {
    // ignore
  }
  return DEFAULT_SETTINGS;
}

function saveSettings(settings: FamilySettings): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // ignore
  }
}

export function getFamilySettings(): FamilySettings {
  return loadSettings();
}

export function updateFamilySettings(updates: Partial<FamilySettings>): FamilySettings {
  const current = loadSettings();
  const next: FamilySettings = {
    notifications: { ...current.notifications, ...updates.notifications },
    privacy: { ...current.privacy, ...updates.privacy },
  };
  saveSettings(next);
  return next;
}

export function resetFamilySettings(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(STORAGE_KEY);
}
